import { CircleArrowUp, LayoutGrid, Map as MapIcon, Search, SearchX, Smartphone } from "lucide-react";
import { lazy, Suspense, useMemo } from "react";
import { Link } from "react-router-dom";

import { DeviceCard } from "@/components/DeviceCard";
import { Button, EmptyState, Loadable, PageHeader, Spinner, StatTile } from "@/components/ui";
import { useAgentInfo } from "@/lib/agent";
import { useAuth } from "@/lib/auth";
import { useFilters } from "@/lib/filters";
import { formatCount } from "@/lib/format";
import { deviceHealth, isAlerting, type Health } from "@/lib/health";
import { useActiveAlerts, useDevices, useFleetSettings, useGroups } from "@/lib/queries";
import type { Device } from "@/lib/types";
import { useNow } from "@/lib/useNow";

// The map brings a large library; it is only loaded when someone opens the map.
const FleetMap = lazy(() => import("@/components/FleetMap").then((m) => ({ default: m.FleetMap })));

type StatusFilter = "all" | "online" | "offline" | "alerting" | "disabled" | "outdated";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Any status" },
  { value: "online", label: "Online" },
  { value: "offline", label: "Offline" },
  { value: "alerting", label: "Needs attention" },
  { value: "disabled", label: "Disabled" },
  { value: "outdated", label: "Agent needs update" },
];

function matches(
  device: Device,
  health: Health,
  status: StatusFilter,
  outdated: (device: Device) => boolean,
): boolean {
  switch (status) {
    case "outdated":
      return outdated(device);
    case "online":
      return device.status === "active" && device.is_online;
    case "offline":
      return device.status === "active" && !device.is_online;
    case "alerting":
      return isAlerting(health);
    case "disabled":
      return device.status === "disabled";
    default:
      return true;
  }
}

export function Fleet() {
  const { can } = useAuth();
  const { latest, isOutdated } = useAgentInfo();
  const devices = useDevices();
  const alerts = useActiveAlerts();
  const groups = useGroups();
  const settings = useFleetSettings();
  const now = useNow(15_000);

  const filters = useFilters({ q: "", status: "all", group: "", shop: "", view: "grid" });
  const { q: search, group, shop } = filters.values;
  const status = filters.values.status as StatusFilter;
  const view = filters.values.view === "map" ? "map" : "grid";
  const set = filters.set;

  const groupNames = useMemo(
    () => new Map(groups.data?.map((item) => [item.id, item.name])),
    [groups.data],
  );
  const shops = useMemo(
    () => [...new Set(devices.data?.flatMap((d) => (d.shop_label ? [d.shop_label] : [])))].sort(),
    [devices.data],
  );

  const all = useMemo(
    () => (devices.data ?? []).map((device) => ({ device, health: deviceHealth(device, alerts.data) })),
    [devices.data, alerts.data],
  );
  const counts = useMemo(
    () => ({
      total: all.length,
      online: all.filter(({ device, health }) => matches(device, health, "online", isOutdated)).length,
      offline: all.filter(({ device, health }) => matches(device, health, "offline", isOutdated)).length,
      alerting: all.filter(({ health }) => isAlerting(health)).length,
      outdated: all.filter(({ device }) => isOutdated(device)).length,
    }),
    [all, isOutdated],
  );

  const shown = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return all
      .filter(({ device, health }) => {
        if (!matches(device, health, status, isOutdated)) return false;
        if (group && device.group_id !== group) return false;
        if (shop && device.shop_label !== shop) return false;
        if (words.length === 0) return true;
        const t = device.latest_telemetry;
        const text = [
          device.name,
          device.shop_label,
          device.shop_location,
          device.model,
          device.serial_number,
          groupNames.get(device.group_id ?? ""),
          // How it is connected: find every device on one Wi-Fi, or by an address.
          t?.wifi_ssid,
          t?.carrier,
          t?.local_ip,
          device.last_ip,
          device.agent_version,
        ]
          .join(" ")
          .toLowerCase();
        return words.every((word) => text.includes(word));
      })
      .sort(
        (a, b) =>
          // What needs attention comes first; then by name.
          Number(isAlerting(b.health)) - Number(isAlerting(a.health)) ||
          a.device.name.localeCompare(b.device.name, undefined, { numeric: true }),
      );
  }, [all, search, status, group, shop, groupNames, isOutdated]);

  const filtered = filters.isActive(["view"]);

  return (
    <>
      <PageHeader
        title="Fleet"
        subtitle="Every device, as it is right now"
        actions={
          <div className="flex rounded-lg border border-axis p-0.5" role="group" aria-label="View">
            <Button
              size="sm"
              variant={view === "grid" ? "secondary" : "ghost"}
              icon={LayoutGrid}
              aria-pressed={view === "grid"}
              onClick={() => set("view", "grid")}
              className="border-0"
            >
              Grid
            </Button>
            <Button
              size="sm"
              variant={view === "map" ? "secondary" : "ghost"}
              icon={MapIcon}
              aria-pressed={view === "map"}
              onClick={() => set("view", "map")}
              className="border-0"
            >
              Map
            </Button>
          </div>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Devices"
          value={formatCount(counts.total)}
          onClick={() => set("status", "all")}
          active={status === "all"}
        />
        <StatTile
          label="Online"
          level="good"
          value={formatCount(counts.online)}
          detail={counts.total ? `${Math.round((100 * counts.online) / counts.total)}% of the fleet` : undefined}
          onClick={() => set("status", "online")}
          active={status === "online"}
        />
        <StatTile
          label="Offline"
          level="neutral"
          value={formatCount(counts.offline)}
          onClick={() => set("status", "offline")}
          active={status === "offline"}
        />
        <StatTile
          label="Need attention"
          level={counts.alerting ? "critical" : "good"}
          value={formatCount(counts.alerting)}
          detail={counts.alerting ? "with unresolved alerts" : "nothing to do"}
          onClick={() => set("status", "alerting")}
          active={status === "alerting"}
        />
      </div>

      {counts.outdated > 0 && status !== "outdated" && (
        <div
          role="status"
          className="card mb-4 flex items-center gap-2 px-4 py-2.5 text-[13px] text-ink-2"
        >
          <CircleArrowUp size={16} className="shrink-0" style={{ color: "var(--color-warning)" }} aria-hidden />
          <span>
            {counts.outdated === 1 ? "1 device runs" : `${counts.outdated} devices run`} an older
            agent than {latest}. Some features stay unavailable on them until they are updated.
          </span>
        </div>
      )}

      {/* One row of filters; everything below follows them. */}
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-52 flex-1">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
          <input
            type="search"
            className="field pl-9"
            placeholder="Search name, shop, model, serial, Wi-Fi, address"
            aria-label="Search devices"
            value={search}
            onChange={(event) => set("q", event.target.value)}
          />
        </div>
        <select className="field w-auto" aria-label="Status" value={status} onChange={(e) => set("status", e.target.value)}>
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select className="field w-auto" aria-label="Group" value={group} onChange={(e) => set("group", e.target.value)}>
          <option value="">Any group</option>
          {groups.data?.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <select className="field w-auto" aria-label="Shop" value={shop} onChange={(e) => set("shop", e.target.value)}>
          <option value="">Any shop</option>
          {shops.map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </select>
        {filtered && (
          <Button variant="ghost" onClick={() => filters.reset(["view"])}>
            Clear
          </Button>
        )}
      </div>

      <Loadable
        query={devices}
        empty={
          devices.data?.length === 0 ? (
            <div className="card">
              <EmptyState
                icon={Smartphone}
                title="No devices yet"
                action={
                  can("admin") ? (
                    <Link to="/admin?tab=enrollment" className="font-medium text-link hover:underline">
                      Create an enrollment code
                    </Link>
                  ) : undefined
                }
              >
                Devices appear here once they are enrolled. An administrator creates an
                enrollment code, and the agent on the device is set up with it.
              </EmptyState>
            </div>
          ) : shown.length === 0 ? (
            <div className="card">
              <EmptyState icon={SearchX} title="No device matches">
                Nothing in the fleet fits these filters.
              </EmptyState>
            </div>
          ) : (
            false
          )
        }
      >
        {filtered && (
          <p className="mb-3 text-[13px] text-muted">
            {shown.length} of {all.length} devices
          </p>
        )}
        {view === "map" ? (
          <Suspense fallback={<Spinner label="Loading the map" />}>
            <FleetMap devices={shown} now={now} />
          </Suspense>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {shown.map(({ device, health }) => (
              <DeviceCard
                key={device.id}
                device={device}
                health={health}
                groupName={groupNames.get(device.group_id ?? "")}
                overheatAt={settings.data?.alert_overheat_celsius ?? 45}
                outdated={isOutdated(device)}
                now={now}
              />
            ))}
          </div>
        )}
      </Loadable>
    </>
  );
}
